import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Trophy, TrendingUp, TrendingDown, Activity, Calendar, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MatchdayDetailsModal = ({ isOpen, onClose, matchday, leagueId, token }) => {
  const [events, setEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchMatchdayEvents = useCallback(async () => {
    if (!matchday?.id) return;
    
    setIsLoadingEvents(true);
    try {
      const response = await fetch(
        `${API_URL}/api/leagues/${leagueId}/matchdays/${matchday.id}/events`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (err) {
      console.error('Error fetching matchday events:', err);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [matchday?.id, leagueId, token]);

  const calculateStats = useCallback(() => {
    if (!matchday?.matches) return;

    const totalMatches = matchday.matches.length;
    const completedMatches = matchday.matches.filter(m => m.status === 'completed').length;
    
    let totalPoints = 0;
    let maxPoints = 0;
    let maxPointsTeam = null;
    
    matchday.matches.forEach(match => {
      const team1Points = match.team1_points || 0;
      const team2Points = match.team2_points || 0;
      totalPoints += team1Points + team2Points;
      
      if (team1Points > maxPoints) {
        maxPoints = team1Points;
        maxPointsTeam = { name: match.team1_name, points: team1Points };
      }
      if (team2Points > maxPoints) {
        maxPoints = team2Points;
        maxPointsTeam = { name: match.team2_name, points: team2Points };
      }
    });

    const avgPoints = completedMatches > 0 ? Math.round(totalPoints / (completedMatches * 2)) : 0;

    setStats({
      totalMatches,
      completedMatches,
      avgPoints,
      topScorer: maxPointsTeam,
    });
  }, [matchday?.matches]);

  useEffect(() => {
    if (isOpen && matchday?.id) {
      fetchMatchdayEvents();
      calculateStats();
    }
  }, [isOpen, matchday?.id, fetchMatchdayEvents, calculateStats]);

  if (!matchday) return null;

  const getResultBadge = (match) => {
    if (match.status !== 'completed') {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700">In Corso</Badge>;
    }
    
    if (match.winner === 'draw') {
      return <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Pareggio</Badge>;
    }
    
    return <Badge variant="outline" className="bg-green-50 text-green-700">Completato</Badge>;
  };

  const getEventIcon = (points) => {
    if (points > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (points < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Activity className="h-4 w-4 text-gray-600" />;
  };

  const getEventColor = (points) => {
    if (points > 0) return 'border-green-200 bg-green-50';
    if (points < 0) return 'border-red-200 bg-red-50';
    return 'border-gray-200 bg-gray-50';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Giornata {matchday.matchday_number}
            {matchday.status === 'pending' ? (
              <Badge className="ml-2 bg-blue-500">In Corso</Badge>
            ) : (
              <Badge className="ml-2 bg-green-500">Completata</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Statistiche Riepilogo */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="text-xs text-blue-600 font-medium mb-1">Match Totali</div>
                <div className="text-2xl font-bold text-blue-900">{stats.totalMatches}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="text-xs text-green-600 font-medium mb-1">Completati</div>
                <div className="text-2xl font-bold text-green-900">{stats.completedMatches}</div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="text-xs text-purple-600 font-medium mb-1">Media Punti</div>
                <div className="text-2xl font-bold text-purple-900">{stats.avgPoints}</div>
              </CardContent>
            </Card>
            
            {stats.topScorer && (
              <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
                <CardContent className="p-4">
                  <div className="text-xs text-yellow-600 font-medium mb-1 flex items-center gap-1">
                    <Trophy className="h-3 w-3" />
                    Top Scorer
                  </div>
                  <div className="text-sm font-bold text-yellow-900 truncate">{stats.topScorer.name}</div>
                  <div className="text-lg font-bold text-yellow-900">{Math.round(stats.topScorer.points)} pt</div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <Tabs defaultValue="matches" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="matches">Match</TabsTrigger>
            <TabsTrigger value="events">Eventi</TabsTrigger>
            <TabsTrigger value="stats">Stats</TabsTrigger>
            <TabsTrigger value="trends">Trend</TabsTrigger>
          </TabsList>

          {/* Tab Match */}
          <TabsContent value="matches" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[400px]">
              <div className="space-y-3 pr-4">
                {matchday.matches?.map((match, idx) => (
                  <Card key={match.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline" className="text-xs">Match {idx + 1}</Badge>
                        {getResultBadge(match)}
                      </div>
                      
                      <div className="flex items-center justify-between gap-4">
                        {/* Team 1 */}
                        <div className="flex items-center gap-3 flex-1">
                          <Avatar className="h-12 w-12 border-2 border-blue-200">
                            <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                              {match.team1_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate">{match.team1_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {match.winner === match.team1_id && '🏆 Vincitore'}
                            </div>
                          </div>
                        </div>

                        {/* Score */}
                        <div className="flex items-center gap-2 px-4">
                          <div className={`text-2xl font-bold ${match.winner === match.team1_id ? 'text-green-600' : 'text-gray-600'}`}>
                            {match.team1_points !== null && match.team1_points !== undefined 
                              ? Math.round(match.team1_points) 
                              : '0.0'}
                          </div>
                          <div className="text-xl text-muted-foreground font-medium">-</div>
                          <div className={`text-2xl font-bold ${match.winner === match.team2_id ? 'text-green-600' : 'text-gray-600'}`}>
                            {match.team2_points !== null && match.team2_points !== undefined 
                              ? Math.round(match.team2_points) 
                              : '0.0'}
                          </div>
                        </div>

                        {/* Team 2 */}
                        <div className="flex items-center gap-3 flex-1 flex-row-reverse">
                          <Avatar className="h-12 w-12 border-2 border-cyan-200">
                            <AvatarFallback className="bg-cyan-100 text-cyan-700 font-semibold">
                              {match.team2_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-semibold text-sm truncate">{match.team2_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {match.winner === match.team2_id && '🏆 Vincitore'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Winner indicator for draw */}
                      {match.winner === 'draw' && match.status === 'completed' && (
                        <div className="mt-3 text-center text-sm text-yellow-600 font-medium">
                          🤝 Pareggio
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tab Eventi */}
          <TabsContent value="events" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[400px]">
              {isLoadingEvents ? (
                <div className="flex justify-center items-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : events.length === 0 ? (
                <div className="text-center text-muted-foreground py-12">
                  Nessun evento in questa giornata
                </div>
              ) : (
                <div className="space-y-2 pr-4">
                  {events.map((event) => (
                    <Card key={event.id} className={`border-l-4 ${getEventColor(event.points)}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            {getEventIcon(event.points)}
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm truncate">
                                {event.professor_name}
                                <span className="text-muted-foreground font-normal ml-2">
                                  ({event.professor_subject})
                                </span>
                              </div>
                              {event.team_name && (
                                <div className="text-xs text-muted-foreground mb-1">
                                  Team: {event.team_name}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground mt-1">
                                {event.description}
                              </div>
                            </div>
                          </div>
                          <div className={`text-lg font-bold whitespace-nowrap ${
                            event.points > 0 ? 'text-green-600' : event.points < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {event.points > 0 ? '+' : ''}{event.points}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Tab Statistiche */}
          <TabsContent value="stats" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[400px]">
              <div className="space-y-4 pr-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-500" />
                      Classifica Giornata
                    </h3>
                    <div className="space-y-2">
                      {matchday.matches
                        ?.flatMap(m => [
                          { name: m.team1_name, points: m.team1_points || 0 },
                          { name: m.team2_name, points: m.team2_points || 0 }
                        ])
                        .sort((a, b) => b.points - a.points)
                        .map((team, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-gray-50 to-white border">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                idx === 1 ? 'bg-gray-200 text-gray-700' :
                                idx === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {idx + 1}
                              </div>
                              <span className="font-medium">{team.name}</span>
                            </div>
                            <div className="text-lg font-bold text-blue-600">
                              {Math.round(team.points)} pt
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                {events.length > 0 && (
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-4">Riepilogo Eventi</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                          <div className="text-2xl font-bold text-green-700">
                            {events.filter(e => e.points > 0).length}
                          </div>
                          <div className="text-sm text-green-600">Bonus</div>
                        </div>
                        <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                          <div className="text-2xl font-bold text-red-700">
                            {events.filter(e => e.points < 0).length}
                          </div>
                          <div className="text-sm text-red-600">Malus</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Tab Trend & Form */}
          <TabsContent value="trends" className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[400px]">
              <div className="space-y-4 pr-4">
                {/* Performance Comparison */}
                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      Performance Match
                    </h3>
                    <div className="space-y-3">
                      {matchday.matches?.map((match, idx) => {
                        const team1Points = match.team1_points || 0;
                        const team2Points = match.team2_points || 0;
                        const maxPoints = Math.max(team1Points, team2Points);
                        const team1Percentage = maxPoints > 0 ? (team1Points / maxPoints) * 100 : 0;
                        const team2Percentage = maxPoints > 0 ? (team2Points / maxPoints) * 100 : 0;

                        return (
                          <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between text-sm font-medium">
                              <span className="text-blue-700">{match.team1_name}</span>
                              <Badge variant="outline" className="text-xs">Match {idx + 1}</Badge>
                              <span className="text-cyan-700">{match.team2_name}</span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {/* Team 1 Bar */}
                              <div className="flex-1">
                                <div className="h-8 bg-gray-100 rounded-full overflow-hidden flex items-center justify-end pr-2">
                                  <div 
                                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                    style={{ width: `${team1Percentage}%` }}
                                  >
                                    <span className="text-white font-bold text-xs">
                                      {Math.round(team1Points)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Separator */}
                              <div className="text-xs font-bold text-gray-400">VS</div>

                              {/* Team 2 Bar */}
                              <div className="flex-1">
                                <div className="h-8 bg-gray-100 rounded-full overflow-hidden flex items-center pl-2">
                                  <div 
                                    className="h-full bg-gradient-to-l from-cyan-400 to-cyan-600 rounded-full flex items-center justify-start pl-2 transition-all duration-500"
                                    style={{ width: `${team2Percentage}%` }}
                                  >
                                    <span className="text-white font-bold text-xs">
                                      {Math.round(team2Points)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Winner Badge */}
                            {match.status === 'completed' && (
                              <div className="text-center">
                                {match.winner === 'draw' ? (
                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                                    🤝 Pareggio
                                  </Badge>
                                ) : (
                                  <Badge className={match.winner === match.team1_id ? 'bg-blue-600' : 'bg-cyan-600'}>
                                    🏆 Vince: {match.winner === match.team1_id ? match.team1_name : match.team2_name}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Performers */}
                {events.length > 0 && (
                  <>
                    <Card className="border-2 border-green-200 bg-green-50/30">
                      <CardContent className="p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2 text-green-700">
                          <TrendingUp className="h-5 w-5" />
                          Top 5 Bonus della Giornata
                        </h3>
                        <div className="space-y-2">
                          {events
                            .filter(e => e.points > 0)
                            .sort((a, b) => b.points - a.points)
                            .slice(0, 5)
                            .map((event, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 flex-1">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                                    idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                                    idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' :
                                    idx === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                                    'bg-gradient-to-br from-green-400 to-green-600'
                                  }`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{event.professor_name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{event.description}</p>
                                  </div>
                                </div>
                                <Badge className="bg-green-600 text-lg font-bold px-3 py-1">
                                  +{event.points}
                                </Badge>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-2 border-red-200 bg-red-50/30">
                      <CardContent className="p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2 text-red-700">
                          <TrendingDown className="h-5 w-5" />
                          Top 5 Malus della Giornata
                        </h3>
                        <div className="space-y-2">
                          {events
                            .filter(e => e.points < 0)
                            .sort((a, b) => a.points - b.points)
                            .slice(0, 5)
                            .map((event, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200 hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center font-bold text-white">
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{event.professor_name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{event.description}</p>
                                  </div>
                                </div>
                                <Badge className="bg-red-600 text-lg font-bold px-3 py-1">
                                  {event.points}
                                </Badge>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
